import { useEffect, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { signUp } from '@/requests/auth'
import { createOrganization } from '@/requests/organization'
import { createShop } from '@/requests/shop'
import {
  Check,
  Store,
  Users,
  Sparkles,
  AlertCircle,
  Loader2,
  Eye,
  EyeOff,
  ArrowRight,
  ArrowLeft,
} from 'lucide-react'
import { useAuthStore } from '@/stores/authStore'
import { cn } from '@/lib/utils'
import { useTranslation } from '@/hooks/use-translation'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { LanguageSwitch } from '@/components/language-switch'

// reCAPTCHA v3 types
declare global {
  interface Window {
    grecaptcha: {
      ready: (callback: () => void) => void
      execute: (siteKey: string, options: { action: string }) => Promise<string>
    }
  }
}

export default function OnboardingStoryboard({
  initialStep,
}: {
  initialStep?: number
}) {
  const { t } = useTranslation()
  // Cap initial step to avoid "Done" state if reloading from old flow
  const [currentStep, setCurrentStep] = useState(Math.min(initialStep || 0, 1))
  const [isRecaptchaVerified, setIsRecaptchaVerified] = useState(false)
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null)
  const [isRecaptchaLoading, setIsRecaptchaLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const { setUser, setAccessToken } = useAuthStore.getState().auth
  const { accessToken } = useAuthStore((state) => state.auth)
  const user = useAuthStore.getState().auth.getUser()

  const recaptchaSiteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY
  const recaptchaVersion = import.meta.env.VITE_RECAPTCHA_VERSION || 'v3'

  const [formData, setFormData] = useState({
    email: user?.email || '',
    password: '',
    fullName: user?.name ? `${user.name} ${user.last_name || ''}`.trim() : '',
    phoneCountry: '+1',
    phoneNumber: user?.mobile_phone || '',
    shopName: '',
    orgId: user?.organization_id || '',
  })

  const [errors, setErrors] = useState<{ [key: string]: string }>({})
  const [touched, setTouched] = useState<{ [key: string]: boolean }>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submissionError, setSubmissionError] = useState('')

  // Load reCAPTCHA v3 script
  useEffect(() => {
    if (recaptchaVersion === 'v3' && recaptchaSiteKey) {
      const script = document.createElement('script')
      script.src = `https://www.google.com/recaptcha/api.js?render=${recaptchaSiteKey}`
      script.async = true
      script.defer = true
      document.body.appendChild(script)

      return () => {
        const existingScript = document.querySelector(
          `script[src*="recaptcha/api.js"]`
        )
        if (existingScript) {
          document.body.removeChild(existingScript)
        }
      }
    }
  }, [recaptchaSiteKey, recaptchaVersion])

  // Sync URL with current step
  useEffect(() => {
    const url = new URL(window.location.href)
    url.searchParams.set('step', currentStep.toString())
    window.history.pushState({}, '', url)

    setErrors({})
    setSubmissionError('')
    if (currentStep !== 0) {
      setIsRecaptchaVerified(false)
      setRecaptchaToken(null)
    }
  }, [currentStep])

  const executeRecaptcha = async (): Promise<string | null> => {
    if (recaptchaVersion !== 'v3' || !recaptchaSiteKey) {
      return null
    }
    return new Promise((resolve) => {
      if (!window.grecaptcha) {
        resolve(null)
        return
      }
      window.grecaptcha.ready(() => {
        window.grecaptcha
          .execute(recaptchaSiteKey, { action: 'signup' })
          .then((token: string) => resolve(token))
          .catch(() => resolve(null))
      })
    })
  }

  const validateField = (name: string, value: string) => {
    const newErrors = { ...errors }

    switch (name) {
      case 'email':
        if (!value) {
          newErrors.email = t('Email is required')
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          newErrors.email = t('Please enter a valid email address')
        } else {
          delete newErrors.email
        }
        break
      case 'password':
        if (!value) {
          newErrors.password = t('Password is required')
        } else if (value.length < 8) {
          newErrors.password = t('Min. 8 characters')
        } else {
          delete newErrors.password
        }
        break
      case 'fullName':
        if (!value.trim()) {
          newErrors.fullName = t('Full name is required')
        } else if (!value.trim().includes(' ')) {
          newErrors.fullName = t('Please enter both first and last name')
        } else {
          delete newErrors.fullName
        }
        break
      case 'phoneNumber':
        if (!value.trim()) {
          newErrors.phoneNumber = t('Phone number is required')
        } else {
          delete newErrors.phoneNumber
        }
        break
      case 'shopName':
        if (!value.trim()) {
          newErrors.shopName = t('Shop name is required')
        } else {
          delete newErrors.shopName
        }
        break
    }

    setErrors(newErrors)
  }

  const handleFieldChange = (name: string, value: string) => {
    setFormData({ ...formData, [name]: value })
    if (touched[name]) {
      validateField(name, value)
    }
  }

  const handleFieldBlur = (name: string) => {
    setTouched({ ...touched, [name]: true })
    validateField(name, formData[name as keyof typeof formData] as string)
  }

  const validateStep = () => {
    const newErrors: { [key: string]: string } = {}

    if (currentStep === 0) {
      if (!formData.fullName.trim()) {
        newErrors.fullName = t('Full name is required')
      } else if (!formData.fullName.trim().includes(' ')) {
        newErrors.fullName = t('Please enter both first and last name')
      }

      if (!formData.email) newErrors.email = t('Email is required')
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = t('Please enter a valid email address')
      }

      if (!formData.password) newErrors.password = t('Password is required')
      else if (formData.password.length < 8) {
        newErrors.password = t('Min. 8 characters')
      }

      if (!formData.phoneNumber.trim())
        newErrors.phoneNumber = t('Phone number is required')

      if (
        recaptchaSiteKey &&
        recaptchaVersion !== 'v3' &&
        !isRecaptchaVerified
      ) {
        newErrors.recaptcha = t('Please complete the reCAPTCHA verification')
      }
    }

    if (currentStep === 1) {
      if (!formData.shopName.trim())
        newErrors.shopName = t('Shop name is required')
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleNext = async () => {
    if (currentStep === 0) {
      Object.keys(formData).forEach((key) => {
        if (['fullName', 'email', 'password', 'phoneNumber'].includes(key)) {
          setTouched((prev) => ({ ...prev, [key]: true }))
        }
      })
    }

    if (!validateStep()) return

    if (currentStep === steps.length - 1) {
      window.location.href = '/settings/shop'
      return
    }

    // Step 0: Sign Up
    if (currentStep === 0) {
      setIsSubmitting(true)
      setSubmissionError('')

      let token = recaptchaToken
      if (recaptchaSiteKey && recaptchaVersion === 'v3' && !token) {
        setIsRecaptchaLoading(true)
        try {
          token = await executeRecaptcha()
          setIsRecaptchaLoading(false)
          if (token) {
            setRecaptchaToken(token)
            setIsRecaptchaVerified(true)
          } else {
            setSubmissionError(
              t('reCAPTCHA verification failed. Please try again.')
            )
            setIsSubmitting(false)
            return
          }
        } catch (error) {
          setIsRecaptchaLoading(false)
          setSubmissionError(
            t('reCAPTCHA verification failed. Please try again.')
          )
          setIsSubmitting(false)
          return
        }
      }

      try {
        const nameParts = formData.fullName.trim().split(' ')
        const firstName = nameParts[0]
        const lastName = nameParts.slice(1).join(' ')

        const payload: any = {
          email: formData.email,
          password: formData.password,
          name: firstName,
          last_name: lastName,
          mobile_phone: `${formData.phoneCountry}${formData.phoneNumber}`,
          language: localStorage.getItem('menu-ui-language') || 'en',
        }

        if (token) payload.recaptcha_token = token

        await signUp(payload).then((response: any) => {
          if (response.token) {
            setAccessToken(response.token)
            localStorage.setItem('sign-up-token', response.token)

            setUser({
              email: response.user.email,
              name: response.user.name,
              id: response.user.id,
            } as any)
          }
        })

        setCurrentStep(currentStep + 1)
      } catch (err: any) {
        console.error('Error signing up:', err)
        let errorMessage = t('Something went wrong. Please try again.')
        if (err.response?.data?.error?.message) {
          errorMessage = err.response.data.error.message
        } else if (err.response?.data?.error?.errors?.[0]) {
          const error = err.response.data.error.errors[0]
          errorMessage = error.msg || error.message || errorMessage
        } else if (err.response?.data?.message) {
          errorMessage = err.response.data.message
        }
        setSubmissionError(errorMessage)
      } finally {
        setIsSubmitting(false)
      }
    }
    // Step 1: Create Organization & Shop
    else if (currentStep === 1) {
      setIsSubmitting(true)
      setSubmissionError('')
      try {
        let currentOrgId = formData.orgId
        const currentLanguage =
          (localStorage.getItem('menu-ui-language') as 'en' | 'tr') || 'en'

        // 1. Create Organization if not exists (using Shop Name)
        if (!currentOrgId) {
          const orgPayload = {
            name: formData.shopName,
            contact_email: formData.email,
            contact_phone: `${formData.phoneCountry}${formData.phoneNumber}`,
          } as any

          const orgResponse = await createOrganization(orgPayload, accessToken)
          if (orgResponse._id) {
            currentOrgId = orgResponse._id
            setFormData((prev) => ({ ...prev, orgId: currentOrgId }))
          }
        }

        // 2. Create Shop
        const shopPayload = {
          name: { [currentLanguage]: formData.shopName },
          organization_id: currentOrgId,
          status: 'ACTIVE',
          contact_information: {
            email: formData.email,
            phone: `${formData.phoneCountry}${formData.phoneNumber}`,
            address: '',
            coordinates: '',
          },
          description: { [currentLanguage]: '' },
          settings: {
            languages: [currentLanguage],
          },
        }

        const createdShop = await createShop(shopPayload, accessToken)

        if (createdShop) {
          const shopToStore = {
            ...createdShop,
            ui_language: currentLanguage,
          }
          localStorage.setItem('lastSelectedShop', JSON.stringify(shopToStore))
          localStorage.setItem('menu-ui-language', currentLanguage)
        }

        setCurrentStep(currentStep + 1)
      } catch (err: any) {
        console.error('Error creating shop/org:', err)
        let errorMessage = t('Something went wrong. Please try again.')
        if (err.response?.data?.error?.message) {
          errorMessage = err.response.data.error.message
        } else if (err.response?.data?.error?.errors?.[0]) {
          const error = err.response.data.error.errors[0]
          errorMessage = error.msg || error.message || errorMessage
        } else if (err.response?.data?.message) {
          errorMessage = err.response.data.message
        }
        setSubmissionError(errorMessage)
      } finally {
        setIsSubmitting(false)
      }
    }
  }

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1)
      setSubmissionError('')
    }
  }

  const steps = [
    {
      title: t('Account'),
      description: t('Create your account'),
      icon: <Users className='h-5 w-5' />,
      content: (
        <div className='space-y-5'>
          <div className='space-y-2'>
            <Label htmlFor='full-name'>
              {t('Full Name')} <span className='text-red-500'>*</span>
            </Label>
            <Input
              id='full-name'
              placeholder='John Doe'
              value={formData.fullName}
              onChange={(e) => handleFieldChange('fullName', e.target.value)}
              onBlur={() => handleFieldBlur('fullName')}
              className={cn(
                errors.fullName && 'border-red-500 focus-visible:ring-red-500'
              )}
            />
            {errors.fullName && (
              <p className='flex items-center gap-1 text-xs text-red-500'>
                <AlertCircle className='h-3 w-3' />
                {errors.fullName}
              </p>
            )}
          </div>
          <div className='space-y-2'>
            <Label htmlFor='email'>
              {t('Email')} <span className='text-red-500'>*</span>
            </Label>
            <Input
              id='email'
              type='email'
              placeholder='you@example.com'
              value={formData.email}
              onChange={(e) => handleFieldChange('email', e.target.value)}
              onBlur={() => handleFieldBlur('email')}
              className={cn(
                errors.email && 'border-red-500 focus-visible:ring-red-500'
              )}
            />
            {errors.email && (
              <p className='flex items-center gap-1 text-xs text-red-500'>
                <AlertCircle className='h-3 w-3' />
                {errors.email}
              </p>
            )}
          </div>
          <div className='space-y-2'>
            <Label htmlFor='password'>
              {t('Password')} <span className='text-red-500'>*</span>
            </Label>
            <div className='relative'>
              <Input
                id='password'
                type={showPassword ? 'text' : 'password'}
                placeholder={t('Min. 8 characters')}
                value={formData.password}
                onChange={(e) => handleFieldChange('password', e.target.value)}
                onBlur={() => handleFieldBlur('password')}
                className={cn(
                  'pr-10',
                  errors.password && 'border-red-500 focus-visible:ring-red-500'
                )}
              />
              <button
                type='button'
                onClick={() => setShowPassword(!showPassword)}
                className='absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground'
              >
                {showPassword ? (
                  <EyeOff className='h-4 w-4' />
                ) : (
                  <Eye className='h-4 w-4' />
                )}
              </button>
            </div>
            {errors.password && (
              <p className='flex items-center gap-1 text-xs text-red-500'>
                <AlertCircle className='h-3 w-3' />
                {errors.password}
              </p>
            )}
          </div>
          <div className='space-y-2'>
            <Label htmlFor='phone'>
              {t('Phone Number')} <span className='text-red-500'>*</span>
            </Label>
            <div className='flex gap-2'>
              <Select
                value={formData.phoneCountry}
                onValueChange={(val) =>
                  setFormData({ ...formData, phoneCountry: val })
                }
              >
                <SelectTrigger className='w-[100px]'>
                  <SelectValue placeholder='+1' />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='+90'>🇹🇷 +90</SelectItem>
                  <SelectItem value='+1'>🇺🇸 +1</SelectItem>
                  <SelectItem value='+44'>🇬🇧 +44</SelectItem>
                  <SelectItem value='+49'>🇩🇪 +49</SelectItem>
                </SelectContent>
              </Select>
              <Input
                id='phone'
                type='tel'
                placeholder='555 123 4567'
                value={formData.phoneNumber}
                onChange={(e) =>
                  handleFieldChange('phoneNumber', e.target.value)
                }
                onBlur={() => handleFieldBlur('phoneNumber')}
                className={cn(
                  'flex-1',
                  errors.phoneNumber &&
                    'border-red-500 focus-visible:ring-red-500'
                )}
              />
            </div>
            {errors.phoneNumber && (
              <p className='flex items-center gap-1 text-xs text-red-500'>
                <AlertCircle className='h-3 w-3' />
                {errors.phoneNumber}
              </p>
            )}
          </div>
          {recaptchaSiteKey &&
            (recaptchaVersion === 'v3' ? (
              <div className='space-y-2'>
                <div className='flex items-center gap-2 rounded-lg border border-border bg-muted p-3 text-sm text-foreground'>
                  <div className='flex-shrink-0'>
                    {isRecaptchaVerified ? (
                      <Check className='h-4 w-4 text-green-600' />
                    ) : (
                      <Loader2
                        className={cn(
                          'h-4 w-4 text-muted-foreground',
                          isRecaptchaLoading && 'animate-spin'
                        )}
                      />
                    )}
                  </div>
                  <div className='flex-1 text-xs'>
                    {isRecaptchaVerified
                      ? t('reCAPTCHA verified')
                      : t('This site is protected by reCAPTCHA.')}
                  </div>
                </div>
                {errors.recaptcha && (
                  <p className='flex items-center gap-1 text-xs text-red-500'>
                    <AlertCircle className='h-3 w-3' />
                    {errors.recaptcha}
                  </p>
                )}
              </div>
            ) : (
              <div className='space-y-2'>
                <div className='mb-2 text-sm text-muted-foreground'>
                  {t('Please complete the reCAPTCHA verification')}
                </div>
                <div id='recaptcha-container'></div>
                {errors.recaptcha && (
                  <p className='flex items-center gap-1 text-xs text-red-500'>
                    <AlertCircle className='h-3 w-3' />
                    {errors.recaptcha}
                  </p>
                )}
              </div>
            ))}
        </div>
      ),
    },
    {
      title: t('Restaurant'),
      description: t('Set up your restaurant'),
      icon: <Store className='h-5 w-5' />,
      content: (
        <div className='space-y-5'>
          <div className='space-y-2'>
            <Label htmlFor='shop-name'>
              {t('Restaurant Name')} <span className='text-red-500'>*</span>
            </Label>
            <Input
              id='shop-name'
              placeholder='My Awesome Burger Place'
              value={formData.shopName}
              onChange={(e) => handleFieldChange('shopName', e.target.value)}
              onBlur={() => handleFieldBlur('shopName')}
              className={cn(
                errors.shopName && 'border-red-500 focus-visible:ring-red-500'
              )}
            />
            {errors.shopName && (
              <p className='flex items-center gap-1 text-xs text-red-500'>
                <AlertCircle className='h-3 w-3' />
                {errors.shopName}
              </p>
            )}
            <p className='text-xs text-muted-foreground'>
              {t('This will be displayed to your customers.')}
            </p>
          </div>
        </div>
      ),
    },
    {
      title: t('Done'),
      description: t('You are ready to go!'),
      icon: <Sparkles className='h-5 w-5' />,
      content: (
        <div className='flex flex-col items-center justify-center space-y-6 py-12'>
          <div className='relative'>
            <div className='rounded-full bg-green-50 p-6'>
              <Check className='h-12 w-12 text-green-600' />
            </div>
          </div>
          <div className='space-y-2 text-center'>
            <h3 className='text-2xl font-semibold text-foreground'>
              {t('All Set!')}
            </h3>
            <p className='mx-auto max-w-xs text-sm text-muted-foreground'>
              {t('Your restaurant has been successfully created.')}
            </p>
          </div>
        </div>
      ),
    },
  ]

  return (
    <div className='flex min-h-screen w-full flex-col items-center justify-center bg-background p-4'>
      <div className='absolute right-4 top-4 z-10 md:hidden'>
        <LanguageSwitch />
      </div>

      <div className='mb-8 max-w-lg text-center'>
        <div className='mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl border border-border bg-card shadow-sm'>
          <img src='/images/favicon.png' alt='Logo' className='h-8 w-8' />
        </div>
        <h1 className='text-2xl font-semibold tracking-tight text-foreground'>
          {t('Welcome to Menu Points')}
        </h1>
        <p className='mt-1 text-sm text-muted-foreground'>
          {t("Let's get your restaurant up and running")}
        </p>
      </div>

      <Card className='w-full max-w-3xl border-border shadow-xl'>
        <div className='grid h-full md:grid-cols-[280px_1fr]'>
          {/* Sidebar Stepper */}
          <div className='hidden border-r border-border bg-muted/50 p-6 md:flex md:flex-col'>
            <nav className='flex flex-1 flex-col space-y-6'>
              <div className='pb-4'>
                <LanguageSwitch />
              </div>
              <div className='space-y-6'>
                {steps.map((step, index) => (
                  <div
                    key={index}
                    className={cn(
                      'relative flex gap-4',
                      index !== steps.length - 1 && 'pb-6'
                    )}
                  >
                    {index !== steps.length - 1 && (
                      <div className='absolute bottom-0 left-[15px] top-8 w-[2px] bg-border' />
                    )}
                    <div
                      className={cn(
                        'relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border transition-all duration-300',
                        index < currentStep
                          ? 'border-primary bg-primary text-primary-foreground'
                          : index === currentStep
                            ? 'border-primary bg-background text-primary shadow-sm ring-2 ring-primary/20'
                            : 'border-muted-foreground/20 bg-background text-muted-foreground'
                      )}
                    >
                      {index < currentStep ? (
                        <Check className='h-4 w-4' />
                      ) : (
                        <span className='text-xs font-medium'>{index + 1}</span>
                      )}
                    </div>
                    <div className='flex flex-col pt-1'>
                      <span
                        className={cn(
                          'text-sm font-medium leading-none transition-colors',
                          index <= currentStep
                            ? 'text-foreground'
                            : 'text-muted-foreground'
                        )}
                      >
                        {step.title}
                      </span>
                      <span className='mt-1 line-clamp-1 text-xs text-muted-foreground'>
                        {step.description}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </nav>
          </div>

          {/* Mobile Progress */}
          <div className='border-b border-border bg-muted/50 p-4 md:hidden'>
            <div className='mb-2 flex items-center justify-between text-sm'>
              <span className='font-medium'>{steps[currentStep].title}</span>
              <span className='text-muted-foreground'>
                {currentStep + 1} / {steps.length}
              </span>
            </div>
            <div className='h-2 w-full overflow-hidden rounded-full bg-border'>
              <div
                className='h-full bg-primary transition-all duration-300'
                style={{
                  width: `${((currentStep + 1) / steps.length) * 100}%`,
                }}
              />
            </div>
          </div>

          <div className='flex h-full flex-col'>
            <CardHeader>
              <CardTitle>{steps[currentStep].title}</CardTitle>
              <CardDescription>
                {steps[currentStep].description}
              </CardDescription>
            </CardHeader>

            <CardContent className='flex-1'>
              {steps[currentStep].content}

              {submissionError && (
                <Alert variant='destructive' className='mt-4'>
                  <AlertCircle className='h-4 w-4' />
                  <AlertDescription>{submissionError}</AlertDescription>
                </Alert>
              )}
            </CardContent>

            <CardFooter className='flex justify-between gap-4 border-t border-border bg-muted/30 p-6'>
              {currentStep === 0 ? (
                <Button variant='ghost' asChild className='gap-2'>
                  <Link to='/sign-in'>
                    <ArrowLeft className='h-4 w-4' />
                    {t('Back to Login')}
                  </Link>
                </Button>
              ) : (
                <Button
                  variant='ghost'
                  onClick={handlePrevious}
                  disabled={isSubmitting}
                  className='gap-2'
                >
                  <ArrowLeft className='h-4 w-4' />
                  {t('Back')}
                </Button>
              )}

              <Button
                onClick={handleNext}
                disabled={
                  isSubmitting ||
                  (currentStep === 0 &&
                    !!recaptchaSiteKey &&
                    recaptchaVersion !== 'v3' &&
                    !isRecaptchaVerified)
                }
                className='min-w-[120px] gap-2'
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className='h-4 w-4 animate-spin' />
                    {t('Please wait')}
                  </>
                ) : currentStep === steps.length - 1 ? (
                  t('Go to Settings')
                ) : (
                  <>
                    {t('Continue')}
                    <ArrowRight className='h-4 w-4' />
                  </>
                )}
              </Button>
            </CardFooter>
          </div>
        </div>
      </Card>
    </div>
  )
}
